import { z } from "zod";
import type { agentServerConfig } from "@/agent/server/config";
import { defaults } from "@/exports/server";
import type { PluginConfig, PluginDefinition } from "@/plugins/server/types";
import { toMethodName } from "@/shared/method-name";
import type { Override } from "@/shared/types";
import type { AgentDefinition, AgentScopeDefinition, TypedAgentBuilder } from "./types";

export class AgentBuilder<const AgentDef extends AgentDefinition, Excluded extends string = never> {
  readonly def: AgentDef;

  constructor(definition: AgentDef) {
    this.def = definition;
  }

  config(config: z.input<typeof agentServerConfig.schema>) {
    const builder = new AgentBuilder({ ...this.def, config });
    const builderWithPlugins = AgentBuilder.withPluginsMethods(builder);
    return builderWithPlugins as unknown as TypedAgentBuilder<AgentDef, Excluded | "config">;
  }

  scope<Schema extends z.ZodObject>(scope: AgentScopeDefinition<Schema>) {
    const builder = new AgentBuilder({ ...this.def, scope });
    const builderWithPlugins = AgentBuilder.withPluginsMethods(builder) as never;
    return builderWithPlugins as TypedAgentBuilder<
      Override<AgentDef, "scope", AgentScopeDefinition<Schema>>,
      Excluded | "scope"
    >;
  }

  /**
   * Register plugins to extend the agent server features.
   * Defaults to `[generation, memories, stores, actions, percepts]` plugins if not specified.
   *
   * In case you want to register custom plugins and still keep the defaults you can do:
   * ```ts
   * import { defaults } from "life/client";
   *
   * defineAgentClient("my-agent").plugins([...defaults.plugins, myCustomPlugin]);
   * ```
   *
   * Or if you want only some of the defaults, you can do:
   * ```ts
   * import { defaults } from "life/client";
   *
   * defineAgentClient("my-agent").plugins([defaults.plugins.generation, defaults.plugins.memories]);
   * ```
   */
  plugins<Plugins extends { def: PluginDefinition }[]>(plugins: Plugins) {
    const builder = new AgentBuilder({ ...this.def, plugins: plugins.map((p) => p.def) });
    const builderWithPlugins = AgentBuilder.withPluginsMethods(builder) as never;
    return builderWithPlugins as TypedAgentBuilder<
      Override<
        Override<
          AgentDef,
          "plugins",
          {
            [K in keyof Plugins]: Plugins[K]["def"];
          }
        >,
        "pluginConfigs",
        {
          [K in Plugins[number] as K["def"]["name"]]: PluginConfig<K["def"]["config"], "output">;
        }
      >,
      Excluded | "plugins"
    >;
  }

  // biome-ignore lint/suspicious/noExplicitAny: reason
  static withPluginsMethods<Builder extends AgentBuilder<any, any>>(builder: Builder) {
    for (const plugin of builder.def.plugins) {
      Object.assign(builder, {
        [toMethodName(plugin.name)]: (
          config: z.input<PluginDefinition["config"]["schema"]>,
        ): unknown => {
          const newBuilder = new AgentBuilder({
            ...builder.def,
            pluginConfigs: {
              ...(builder.def.pluginConfigs ?? {}),
              [plugin.name]: config,
            },
          });
          return AgentBuilder.withPluginsMethods(newBuilder);
        },
      });
    }
    return builder;
  }
}

export function defineAgent<const Name extends string>(name: Name) {
  const defaultDefinition = {
    name,
    config: {},
    scope: { schema: z.object(), hasAccess: () => true },
    plugins: [...defaults.plugins].map((p) => p.def),
    pluginConfigs: {},
  } as const satisfies AgentDefinition;
  const builder = new AgentBuilder(defaultDefinition);
  const builderWithPlugins = AgentBuilder.withPluginsMethods(builder);
  return builderWithPlugins as TypedAgentBuilder<typeof builderWithPlugins.def, never>;
}
